import { useEffect, useState } from "react";
import { XIcon } from "lucide-react";
import type { PayrollDeductionDraft } from "../../types";

type DeductionFormProps = {
  isOpen: boolean;
  onClose: () => void;
  employeeName: string;
  initialValue?: PayrollDeductionDraft | null;
  onSave?: (input: PayrollDeductionDraft) => void | Promise<void>;
};

const deductionTypes = [
  { value: "Health Insurance", defaultTax: "Pre-Tax" as const },
  { value: "Dental", defaultTax: "Pre-Tax" as const },
  { value: "Vision", defaultTax: "Pre-Tax" as const },
  { value: "401k/403b", defaultTax: "Pre-Tax" as const },
  { value: "FSA", defaultTax: "Pre-Tax" as const },
  { value: "Childcare", defaultTax: "Pre-Tax" as const },
  { value: "Union Dues", defaultTax: "Post-Tax" as const },
  { value: "Tzedakah", defaultTax: "Post-Tax" as const },
  { value: "Custom", defaultTax: "Post-Tax" as const },
] as const;

export function DeductionForm({
  isOpen,
  onClose,
  employeeName,
  initialValue,
  onSave,
}: DeductionFormProps) {
  const [deductionType, setDeductionType] =
    useState<NonNullable<PayrollDeductionDraft["deductionType"]>>(
      "Health Insurance",
    );
  const [customTypeName, setCustomTypeName] = useState("");
  const [taxClassification, setTaxClassification] =
    useState<NonNullable<PayrollDeductionDraft["taxClassification"]>>(
      "Pre-Tax",
    );
  const [amount, setAmount] = useState("");
  const [isRecurring, setIsRecurring] = useState(true);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setDeductionType(initialValue?.deductionType ?? "Health Insurance");
    setCustomTypeName(initialValue?.customTypeName ?? "");
    setTaxClassification(initialValue?.taxClassification ?? "Pre-Tax");
    setAmount(
      initialValue?.amount == null ? "" : String(initialValue.amount),
    );
    setIsRecurring(initialValue?.isRecurring ?? true);
  }, [initialValue, isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleDeductionTypeChange = (type: string) => {
    const nextType = type as NonNullable<PayrollDeductionDraft["deductionType"]>;
    setDeductionType(nextType);
    const selectedType = deductionTypes.find((entry) => entry.value === type);
    if (selectedType) {
      setTaxClassification(selectedType.defaultTax);
    }
  };

  const isValid =
    (deductionType !== "Custom" || customTypeName !== "") &&
    amount !== "" &&
    Number(amount) > 0;

  const handleSave = async () => {
    if (!isValid) {
      return;
    }
    await onSave?.({
      ...initialValue,
      deductionType,
      customTypeName: deductionType === "Custom" ? customTypeName : undefined,
      taxClassification,
      amount: Number(amount),
      isRecurring,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative flex max-h-[90vh] w-full max-w-[600px] flex-col overflow-hidden rounded-[12px] bg-surface-light shadow-xl animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between border-b border-border bg-muted/30 p-5">
          <div>
            <h2 className="font-heading text-lg font-semibold text-iron-grey">
              {initialValue?.id ? "Edit Deduction" : "Add Deduction"}
            </h2>
            <p className="mt-0.5 font-body text-sm text-text-secondary">
              For {employeeName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary transition-colors hover:text-iron-grey"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 overflow-y-auto p-6">
          <div>
            <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
              Deduction Type
            </label>
            <select
              value={deductionType}
              onChange={(event) => handleDeductionTypeChange(event.target.value)}
              className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {deductionTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.value}
                </option>
              ))}
            </select>
          </div>

          {deductionType === "Custom" ? (
            <div className="animate-in slide-in-from-top-2 duration-200">
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                Custom Deduction Name
              </label>
              <input
                type="text"
                value={customTypeName}
                onChange={(event) => setCustomTypeName(event.target.value)}
                placeholder="e.g. Parking Fee"
                className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                Tax Classification
              </label>
              <select
                value={taxClassification}
                onChange={(event) =>
                  setTaxClassification(
                    event.target
                      .value as NonNullable<
                      PayrollDeductionDraft["taxClassification"]
                    >,
                  )
                }
                className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="Pre-Tax">Pre-Tax</option>
                <option value="Post-Tax">Post-Tax</option>
              </select>
            </div>
            <div>
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                Per-Period Amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-body text-sm text-text-secondary">
                  $
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  placeholder="0.00"
                  className="w-full rounded-[6px] border border-border bg-surface-light py-2 pl-7 pr-3 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-[8px] bg-muted/30 p-4">
            <div>
              <label className="font-body text-sm font-medium text-iron-grey">
                Apply this deduction every pay period
              </label>
              <p className="mt-0.5 font-body text-xs text-text-secondary">
                Recurring deductions auto-apply to future periods
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsRecurring((current) => !current)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isRecurring ? "bg-primary" : "bg-border"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                  isRecurring ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-border bg-background-light p-5">
          <button
            onClick={onClose}
            className="rounded-[6px] border border-border bg-surface-light px-4 py-2 font-body text-sm font-medium text-iron-grey transition-colors hover:bg-muted"
          >
            Cancel
          </button>
          <button
            disabled={!isValid}
            onClick={() => void handleSave()}
            className="rounded-[6px] bg-primary px-4 py-2 font-body text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Save Deduction
          </button>
        </div>
      </div>
    </div>
  );
}
